import { FC, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faRobot } from '@fortawesome/free-solid-svg-icons';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import FilterResultAI from './FilterResultAI';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import {
  chatKeyForPath,
  getChat,
  getWidgetOpen,
  setWidgetOpen,
  subscribeToChats,
  subscribeToWidget,
  updateChat,
} from 'utils/assistantChatStore';
import { isAiAssistantEnabledFromBranch } from 'utils/hotel';

interface AssistantWidgetProps {
  // Route prefixes where the widget stays out of the way (login, payment return pages, printable pages ...).
  hideOnPaths?: string[];
}

const isUnder = (pathname: string, prefix: string) =>
  pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`);

/**
 * The AI Assistant on every page: a round button fixed at the bottom right that opens the chat upwards. The chat is the
 * same one as the tab in the results page's left panel (same conversation, same session), which stays where it is.
 * It only shows for a signed-in user, and the chat - and with it the assistant session - is created the first time the
 * widget is opened, not on every page load.
 * Where it sits is set with the CSS variables of .assistant-widget (shared/assistant.scss): by default it keeps clear of the
 * accessibility button and the reCAPTCHA badge that share that corner.
 */
const AssistantWidget: FC<AssistantWidgetProps> = ({ hideOnPaths = [] }) => {
  const { t } = useTranslation();
  const config = useContext(MainContext);
  const { pathname } = useLocation();
  const chatKey = chatKeyForPath(pathname);
  const open = useSyncExternalStore(subscribeToWidget, getWidgetOpen);
  const { busy, unread } = useSyncExternalStore(subscribeToChats, () => getChat(chatKey));
  // Kept mounted once it has been opened, so the draft and the scroll position survive minimising.
  const [everOpened, setEverOpened] = useState(open);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const fabRef = useRef<HTMLButtonElement | null>(null);
  const focusChatOnOpen = useRef(false);

  const assistantEnabled = isAiAssistantEnabledFromBranch(config.user?.branch?.ext_1_pcc);
  const visible =
    !!config.user?.isAuth &&
    assistantEnabled &&
    !hideOnPaths.some((prefix) => isUnder(pathname, prefix));

  // Opened: the chat is built (once), and whatever arrived meanwhile counts as read.
  useEffect(() => {
    if (!open) {
      return;
    }
    setEverOpened(true);
    if (unread > 0) {
      updateChat(chatKey, (chat) => ({ ...chat, unread: 0 }));
    }
  }, [open, chatKey, unread]);

  // Opened by the user (not restored after a reload): the cursor goes to the question box.
  useEffect(() => {
    if (!open || !focusChatOnOpen.current) {
      return undefined;
    }
    focusChatOnOpen.current = false;
    // The panel needs a moment to become visible, and on the very first opening the box stays disabled until the
    // session exists; a hidden or disabled input cannot take focus, so try for a couple of seconds.
    let tries = 0;
    const timer = window.setInterval(() => {
      const input = panelRef.current?.querySelector('input');
      if (input && !input.disabled) {
        input.focus();
        window.clearInterval(timer);
      } else if (++tries >= 12) {
        window.clearInterval(timer);
      }
    }, 150);
    return () => window.clearInterval(timer);
  }, [open]);

  // Escape minimises the widget when the focus is inside it. It leaves Escape alone while the Session Viewer (which
  // closes itself with it) is on top.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || document.querySelector('.assistant-viewer-overlay')) {
        return;
      }
      if (panelRef.current?.contains(document.activeElement)) {
        setWidgetOpen(false);
        fabRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!visible) {
    return null;
  }

  const toggle = () => {
    const next = !getWidgetOpen();
    focusChatOnOpen.current = next;
    setWidgetOpen(next);
    if (!next) {
      fabRef.current?.focus();
    }
  };

  const hasUnread = unread > 0 && !open;
  const working = busy && !open;

  return (
    <div className={'assistant-widget' + (open ? ' is-open' : '')}>
      {everOpened && (
        <div
          id="assistant-widget-panel"
          ref={panelRef}
          className="assistant-widget-panel"
          role="dialog"
          aria-label={t('assistant.widget_title')}
          aria-hidden={!open}
        >
          <div className="assistant-widget-head">
            <span className="assistant-widget-title">
              <FontAwesomeIcon icon={faRobot} />
              {t('assistant.widget_title')}
            </span>
            <button
              type="button"
              className="assistant-widget-minimize"
              onClick={toggle}
              aria-label={t('assistant.widget_close')}
              title={t('assistant.widget_close')}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>
          <div className="assistant-widget-body">
            {/* The chat of this page's search ("Hotels 2" has its own); the widget itself sits above every route. */}
            <HotelConfigContext.Provider value={{ hotelIndex: chatKey === 2 ? 2 : undefined }}>
              <FilterResultAI variant="widget" />
            </HotelConfigContext.Provider>
          </div>
        </div>
      )}
      <button
        ref={fabRef}
        type="button"
        className={'assistant-widget-fab' + (working ? ' is-working' : '')}
        onClick={toggle}
        aria-expanded={open}
        aria-controls="assistant-widget-panel"
        aria-label={open ? t('assistant.widget_close') : t('assistant.widget_open')}
        title={open ? t('assistant.widget_close') : t('assistant.widget_open')}
      >
        <span className="assistant-widget-fab-icon assistant-widget-fab-icon--bot" aria-hidden="true">
          AI
        </span>
        <span className="assistant-widget-fab-icon assistant-widget-fab-icon--close" aria-hidden="true">
          <FontAwesomeIcon icon={faChevronDown} />
        </span>
        {working && <span className="assistant-widget-fab-ring" aria-hidden="true" />}
        {hasUnread && <span className="assistant-widget-fab-dot" aria-hidden="true" />}
      </button>
      <span className="assistant-sr-only" role="status" aria-live="polite">
        {hasUnread ? t('assistant.widget_new_reply') : ''}
      </span>
    </div>
  );
};

export default withErrorBoundary(AssistantWidget, 'AssistantWidget');
